import { useEffect, useState } from "react";
import api, { eur } from "@/lib/api";
import { PageHeader, MonthSelector, Card, Kpi, thisMonth, ExportButton } from "@/components/common";
import { downloadCSV } from "@/lib/csv";
import { subLabel } from "@/lib/expenseConstants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const EXP_COLORS = ["#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#6366f1", "#14b8a6"];

export default function Report() {
  const [month, setMonth] = useState(thisMonth());
  const [r, setR] = useState(null);
  const [cats, setCats] = useState([]);
  const [adminCats, setAdminCats] = useState([]);

  useEffect(() => {
    api.get(`/report?month=${month}`).then((res) => setR(res.data));
  }, [month]);
  useEffect(() => { api.get("/guesthouse-categories").then((res) => setCats(res.data)); }, []);
  useEffect(() => { api.get("/administration-categories").then((res) => setAdminCats(res.data)); }, []);

  if (!r) return null;
  const s = r.spese;
  const gh = s.guesthouse;
  const adm = s.amministrazione || { total: 0 };
  const catLabel = (v) => cats.find((c) => c.value === v)?.label || subLabel(v);
  const admLabel = (v) => adminCats.find((c) => c.value === v)?.label || v;
  const ghRows = Object.entries(gh).filter(([k]) => k !== "total").map(([k, v]) => [catLabel(k), v]);
  const admRows = Object.entries(adm).filter(([k]) => k !== "total").map(([k, v]) => [admLabel(k), v]);
  const expenseData = [
    { name: "Maldive Autentiche", value: s.maldive_autentiche },
    ...Object.entries(s.manual || {}).map(([k, v]) => ({ name: k, value: v })),
    { name: "Affitti", value: s.affitti },
    { name: "Staff", value: s.staff },
    { name: "Costi guesthouse Keyodhoo", value: s.keyodhoo_costi },
    { name: "Guesthouse", value: gh.total },
    { name: "Amministrazione", value: adm.total },
    { name: "MIRA", value: s.mira },
  ].filter((d) => d.value > 0);
  const propData = Object.entries(r.per_property || {}).map(([k, v]) => ({ name: k, entrate: v.entrate }));

  const exportCSV = () => {
    const rows = [
      ["Mese", month],
      ["Entrate", r.entrate],
      ["Maldive Autentiche", s.maldive_autentiche],
      ...Object.entries(s.manual || {}),
      ["Affitti", s.affitti],
      ["Staff", s.staff],
      ["Spese Guesthouse", gh.total],
      ...ghRows.map(([k, v]) => [`  - ${k}`, v]),
      ["Amministrazione", adm.total],
      ...admRows.map(([k, v]) => [`  - ${k}`, v]),
      ["MIRA", s.mira],
      ["Totale spese", s.total],
      ["Netto", r.netto],
      [],
      ["Origine", "Entrate", "Commissione", "Prenotazioni"],
      ...Object.entries(r.per_agency).map(([k, v]) => [k, v.entrate, v.commissione, v.count]),
    ];
    downloadCSV(`report_${month}.csv`, ["Voce", "Valore (€)"], rows);
  };

  return (
    <div>
      <PageHeader title="Report" subtitle="Conto economico mensile · netto in mano">
        <MonthSelector value={month} onChange={setMonth} />
        <ExportButton onClick={exportCSV} testid="export-report" label="Scarica report CSV" />
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi testid="rep-entrate" label="Entrate (€)" value={eur(r.entrate)} accent="text-sky-700" />
        <Kpi testid="rep-spese" label="Totale spese" value={eur(s.total)} accent="text-red-600" />
        <Kpi testid="rep-netto" label="Netto" value={eur(r.netto)} accent={r.netto >= 0 ? "text-emerald-600" : "text-red-600"} />
        <Kpi testid="rep-pren" label="Prenotazioni" value={r.bookings_count} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5" data-testid="revenue-per-property">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Revenue per proprietà</h3>
          {propData.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">Nessuna prenotazione nel mese</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={propData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => eur(v)} />
                <Bar dataKey="entrate" name="Entrate" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-5" data-testid="expense-pie">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Ripartizione spese</h3>
          {expenseData.length === 0 ? (
            <p className="text-sm text-slate-400 py-12 text-center">Nessuna spesa nel mese</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={expenseData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {expenseData.map((e, i) => <Cell key={i} fill={EXP_COLORS[i % EXP_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => eur(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Voci di spesa</h3>
          <table className="w-full text-sm">
            <tbody>
              {[
                ["Maldive Autentiche", s.maldive_autentiche],
                ...Object.entries(s.manual || {}),
                ["Affitti", s.affitti],
                ["Staff", s.staff],
              ].map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100">
                  <td className="py-2 text-slate-600">{k}</td>
                  <td className="py-2 text-right tabular font-medium">{eur(v)}</td>
                </tr>
              ))}
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-800 font-semibold">Spese Guesthouse</td>
                <td className="py-2 text-right tabular font-semibold">{eur(gh.total)}</td>
              </tr>
              {ghRows.map(([k, v]) => (
                <tr key={k} className="border-b border-slate-50">
                  <td className="py-1.5 pl-4 text-slate-500 text-xs">— {k}</td>
                  <td className="py-1.5 text-right tabular text-slate-500 text-xs">{eur(v)}</td>
                </tr>
              ))}
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-800 font-semibold">Amministrazione</td>
                <td className="py-2 text-right tabular font-semibold">{eur(adm.total)}</td>
              </tr>
              {admRows.map(([k, v]) => (
                <tr key={`adm-${k}`} className="border-b border-slate-50">
                  <td className="py-1.5 pl-4 text-slate-500 text-xs">— {k}</td>
                  <td className="py-1.5 text-right tabular text-slate-500 text-xs">{eur(v)}</td>
                </tr>
              ))}
              <tr className="border-b border-slate-100">
                <td className="py-2 text-slate-600">MIRA</td>
                <td className="py-2 text-right tabular font-medium">{eur(s.mira)}</td>
              </tr>
              <tr className="font-bold">
                <td className="py-2.5">Totale spese</td>
                <td className="py-2.5 text-right tabular text-red-600">{eur(s.total)}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Dettaglio per origine prenotazione</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <th className="text-left py-2">Origine</th>
                <th className="text-right py-2">Pren.</th>
                <th className="text-right py-2">Entrate</th>
                <th className="text-right py-2">Commissione</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(r.per_agency).map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100">
                  <td className="py-2 text-slate-700">{k}</td>
                  <td className="py-2 text-right tabular">{v.count}</td>
                  <td className="py-2 text-right tabular">{eur(v.entrate)}</td>
                  <td className="py-2 text-right tabular text-slate-600">{eur(v.commissione)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-400 mt-4">
            Le commissioni Viaggiacolpasta / Maldive da vivere / Un'Emozione / Diretto confluiscono in
            "Maldive Autentiche". Dea Maldives ha una voce dedicata alimentata dalle commissioni inserite in prenotazione.
          </p>
        </Card>
      </div>

      <div className="mt-6 bg-slate-900 text-white rounded-md p-6 flex items-center justify-between">
        <div>
          <div className="text-sm text-slate-300 uppercase tracking-wide">Netto in mano</div>
          <div className="text-xs text-slate-400 mt-1">Entrate − tutte le spese</div>
        </div>
        <div className={`font-heading text-4xl font-extrabold tabular ${r.netto >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="rep-netto-big">
          {eur(r.netto)}
        </div>
      </div>
    </div>
  );
}
